/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import _ from 'lodash';
import {
  AreaWiseAggregations,
  IAny,
  ICost,
  IEnergyMeter,
  IObject
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  IEnergyDetailsForOtherTabs,
  IEnergyTotalUsagesAreasDetails,
  IUtilityArea,
  IUtilityMeterForArea
} from '../../DetailKPIWidgets/EnergyUsagesViewDetailsWidget/AllUtilitiesDetails/IEnergyUsagesBreakdownProps';
import {
  ElectricUnit,
  EnergyUsageScopeType,
  GasUnit,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from './Constants';
import {
  getConvertedValuesViewDetails,
  getMaxBreakdownByMeter,
  totalDeviationPercentageCheck,
  convertGasValueCheck
} from './EnergyUsagesViewDetailsUtils';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from './unitHandling/unitHandling';
import { ConversionType } from './unitHandling/UnitModel';

export const getUtilityUnit = (emissionsByUtility: ICost[]) => {
  const utilityUnit: IObject = {};
  emissionsByUtility?.forEach((data) => {
    const { sourceType, measurementUnit } = data;
    const key = sourceType?.toLowerCase();
    utilityUnit[key] = measurementUnit;
  });
  return { utilityUnit };
};

export const getMaxUtilityUnit = (
  areaWiseAggregations: AreaWiseAggregations[],
  utilityUnit: {
    [key: string]: string;
  },
  convertFromUnit: {
    [key: string]: string;
  }
) => {
  const ElectricValuesArray: (number | null)[] = [];
  const GasValuesArray: (number | null)[] = [];
  areaWiseAggregations?.forEach((areaData) => {
    ElectricValuesArray.push(
      areaData.totalConsumption,
      areaData.totalBaselineConsumption
    );
    areaData.meterWiseAggregations?.forEach((meterData) => {
      if (
        meterData.sourceType &&
        meterData.sourceType.toLowerCase().includes(ElectricUnit)
      ) {
        ElectricValuesArray.push(
          meterData.totalConsumption,
          meterData.totalBaselineConsumption
        );
      }
      if (
        meterData.sourceType &&
        meterData.sourceType.toLowerCase().includes(GasUnit)
      ) {
        GasValuesArray.push(
          meterData.totalConsumption,
          meterData.totalBaselineConsumption
        );
      }
    });
  });
  const maxValueUnitConversionElectric = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    convertFromUnit[ElectricUnit],
    ElectricValuesArray
  );
  const maxValueUnitConversionGas = getMaxConvertedValueFromArray(
    ConversionType.GAS,
    utilityUnit[GasUnit],
    GasValuesArray
  );

  return { maxValueUnitConversionElectric, maxValueUnitConversionGas };
};

const getConversionType = (element: any) => {
  if (element.sourceType && element.sourceType.toLowerCase() === 'gas') {
    return ConversionType.GAS;
  }
  return ConversionType.ELECTRICITY;
};

const getTotalDeviationPercentage = (element: any) => {
  return element.totalEnergyDeviationPercentage === null
    ? null
    : parseFloat(
        element.totalEnergyDeviationPercentage?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      );
};

const getRowDataForNoDeviation = (
  element: any,
  rowData: any,
  conversionType: any,
  convertfromunit: any,
  toUnit?: string
) => {
  if (
    rowData.totalDeviation === 0 &&
    element.totalNativeEnergyDeviation !== 0
  ) {
    return {
      ...rowData,
      deviationScaledData: getConvertedValue(
        conversionType,
        convertfromunit,
        element.totalNativeEnergyDeviation,
        toUnit
      )
    };
  }
  return rowData;
};

const getPercentageEmissionSavings = (meters: any) => {
  return meters.totalEnergyDeviationPercentage === null
    ? null
    : parseFloat(
        meters.totalEnergyDeviationPercentage?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      );
};

const getUtilityDataForNoDeviation = (
  meters: any,
  utilityData: any,
  convertfromunit: any,
  toUnit?: string
) => {
  if (
    utilityData.totalDeviation === 0 &&
    meters.totalNativeEnergyDeviation !== 0
  ) {
    return {
      ...utilityData,
      deviationScaledData: getConvertedValue(
        ConversionType?.ELECTRICITY,
        convertfromunit.electric,
        meters.totalNativeEnergyDeviation,
        toUnit
      )
    };
  }
  return utilityData;
};

const getUtilityDataForNoDeviationForOtherTabs = (
  meters: any,
  utilityData: any,
  convertfromunit: any,
  currentUtility: any,
  toUnit?: string
) => {
  if (
    utilityData.totalDeviation === 0 &&
    meters.totalNativeEnergyDeviation !== 0
  ) {
    return {
      ...utilityData,
      deviationScaledData: getConvertedValue(
        currentUtility === ConversionType.GAS
          ? ConversionType.GAS
          : ConversionType.ELECTRICITY,
        convertfromunit,
        meters.totalNativeEnergyDeviation,
        toUnit
      )
    };
  }
  return utilityData;
};

export const scaleDataForOtherUtility = (
  meters: Array<{ [key: string]: string | number }>,
  sourceType: string,
  fromUnit: string,
  energyMeasurementUnit: string,
  showChangeWhenData?: boolean,
  zoneRawActualEmission?: number
) => {
  const convertElectricValue = (value: number | null, convertUnit: string) => {
    if (!value) return value;
    else {
      return getConvertedValue(
        ConversionType?.ELECTRICITY,
        energyMeasurementUnit,
        value,
        convertUnit
      ).Value;
    }
  };

  const convertGasValue = (value: number | null, convertUnit: string) => {
    return convertGasValueCheck(
      ConversionType?.GAS,
      energyMeasurementUnit,
      value,
      convertUnit
    );
  };

  let conversionType = ConversionType.ELECTRICITY;
  if (sourceType && sourceType.toLowerCase() === 'gas') {
    conversionType = ConversionType.GAS;
  }
  if (sourceType && sourceType.toLowerCase() === 'water') {
    conversionType = ConversionType.WATER;
  }
  const utilityMeterDetail: IAny = [];
  console.log('meterssssss', meters);
  meters.forEach((element: IAny) => {
    const maxUnit = getMaxBreakdownByMeter([element]);
    const maxValueUnitConversion = getMaxConvertedValueFromArray(
      conversionType,
      fromUnit,
      [maxUnit],
      energyMeasurementUnit
    );
    // convertedUnit = maxValueUnitConversion.Unit;
    let rowData;
    console.log('element.sourceType', element.sourceType);
    let isExcludeBaseline = false;
    try {
      isExcludeBaseline =
        element.tags &&
        _.isArray(element.tags) &&
        window.ENV &&
        window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST &&
        _.some(element.tags, (t) =>
          window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST.split(',').includes(
            t
          )
        );
    } catch (e) {
      console.log('some error with exclude baseline check', e);
    }
    if (
      element.sourceType.toLowerCase() === EnergyUsageScopeType.ELECTRIC ||
      element.sourceType === EnergyUsageScopeType.ELECTRICITY
    ) {
      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(element.totalConsumption)) ||
          isNaN(Number(element.totalBaselineConsumption)) ||
          Number(element.totalConsumption) === 0 ||
          Number(element.totalBaselineConsumption) === 0 ||
          Math.abs(Number(element.totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);
      rowData = {
        meterId: element.id,
        meterName: element.name,
        utilityType: element.sourceType,
        totalActualEmission: element.totalConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalConsumption],
          energyMeasurementUnit
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalBaselineConsumption],
          energyMeasurementUnit
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalEnergyDeviation],
          energyMeasurementUnit
        ).Unit,
        totalPreviousPeriodEmission: element.totalBaselineConsumption,
        totalAbsoluteBaselineConsumption: convertElectricValue(
          element.totalBaselineConsumption,
          maxValueUnitConversion.Unit
        ),
        totalAbsoluteConsumption: convertElectricValue(
          element.totalConsumption,
          maxValueUnitConversion.Unit
        ),
        totalDeviationPercentage: totalDeviationPercentageCheck(
          element.totalEnergyDeviationPercentage
        ),
        totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
        rawTotalActualEmission: element.totalConsumption,
        rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
        rawTotalAbsoluteConsumption: element.totalConsumption,
        rawTotalAbsoluteBaselineConsumption: element.totalBaselineConsumption,
        rawTotalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
        unit: maxValueUnitConversion.Unit,
        progessBarRelativeWidth: zoneRawActualEmission,
        isExcludeBaseline
      };
      rowData = getRowDataForNoDeviation(
        element,
        rowData,
        ConversionType?.ELECTRICITY,
        energyMeasurementUnit,
        maxValueUnitConversion.Unit
      );
      // if (
      //   rowData.totalDeviation === 0 &&
      //   element.totalNativeEnergyDeviation !== 0
      // ) {
      //   rowData = {
      //     ...rowData,
      //     deviationScaledData: getConvertedValue(
      //       ConversionType?.ELECTRICITY,
      //       convertfromunit,
      //       element.totalNativeEnergyDeviation
      //     )
      //   };
      // }
    }
    if (element.sourceType.toLowerCase() === 'gas') {
      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(element.totalConsumption)) ||
          isNaN(Number(element.totalBaselineConsumption)) ||
          Number(element.totalConsumption) === 0 ||
          Number(element.totalBaselineConsumption) === 0 ||
          Math.abs(Number(element.totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);

      rowData = {
        meterId: element.id,
        meterName: element.name,
        utilityType: element.sourceType,
        totalActualEmission: element.totalConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalConsumption],
          energyMeasurementUnit
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalBaselineConsumption],
          energyMeasurementUnit
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalEnergyDeviation],
          energyMeasurementUnit
        ).Unit,
        totalPreviousPeriodEmission: element.totalBaselineConsumption,
        totalAbsoluteBaselineConsumption: convertGasValue(
          element.totalAbsoluteBaselineConsumption,
          maxValueUnitConversion.Unit
        ),
        totalAbsoluteConsumption: convertGasValue(
          element.totalAbsoluteConsumption,
          maxValueUnitConversion.Unit
        ),
        totalDeviationPercentage: totalDeviationPercentageCheck(
          element.totalEnergyDeviationPercentage
        ),
        totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
        rawTotalActualEmission: element.totalConsumption,
        rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
        rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
        rawTotalAbsoluteBaselineConsumption:
          element.totalAbsoluteBaselineConsumption,
        rawTotalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
        unit: maxValueUnitConversion.Unit,
        isExcludeBaseline
      };

      rowData = getRowDataForNoDeviation(
        element,
        rowData,
        ConversionType?.GAS,
        energyMeasurementUnit
      );
      // if (
      //   rowData.totalDeviation === 0 &&
      //   element.totalNativeEnergyDeviation !== 0
      // ) {
      //   rowData = {
      //     ...rowData,
      //     deviationScaledData: getConvertedValue(
      //       ConversionType?.GAS,
      //       convertfromunit,
      //       element.totalNativeEnergyDeviation
      //     )
      //   };
      // }
    }
    if (element.sourceType.toLowerCase() === 'water') {
      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(element.totalConsumption)) ||
          isNaN(Number(element.totalBaselineConsumption)) ||
          Number(element.totalConsumption) === 0 ||
          Number(element.totalBaselineConsumption) === 0 ||
          Math.abs(Number(element.totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);
      rowData = {
        meterId: element.id,
        meterName: element.name,
        utilityType: element.sourceType,
        totalActualEmission: element.totalConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalConsumption],
          energyMeasurementUnit
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalBaselineConsumption],
          energyMeasurementUnit
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          conversionType,
          fromUnit,
          [element.totalEnergyDeviation],
          energyMeasurementUnit
        ).Unit,
        totalPreviousPeriodEmission: element.totalBaselineConsumption,
        totalAbsoluteBaselineConsumption: convertGasValue(
          element.totalAbsoluteBaselineConsumption,
          maxValueUnitConversion.Unit
        ),
        totalAbsoluteConsumption: convertGasValue(
          element.totalAbsoluteConsumption,
          maxValueUnitConversion.Unit
        ),
        totalDeviationPercentage: totalDeviationPercentageCheck(
          element.totalEnergyDeviationPercentage
        ),
        totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
        rawTotalActualEmission: element.totalConsumption,
        rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
        rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
        rawTotalAbsoluteBaselineConsumption:
          element.totalAbsoluteBaselineConsumption,
        rawTotalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
        unit: maxValueUnitConversion.Unit,
        isExcludeBaseline
      };

      rowData = getRowDataForNoDeviation(
        element,
        rowData,
        ConversionType.WATER,
        energyMeasurementUnit
      );
    }
    utilityMeterDetail.push(rowData as IUtilityMeterForArea);
  });
  return utilityMeterDetail;
};

export const scaleData = (
  meters: IAny,
  userPreferedUnit: string,
  energyMeasurementUnit: string,
  showChangeWhenData?: boolean,
  rawActualEmission?: number
) => {
  const convertElectricityValue = (
    value: number | null,
    convertUnit: string
  ) => {
    if (!value) {
      return value;
    } else {
      return getConvertedValuesViewDetails(
        value,
        ConversionType?.ELECTRICITY,
        energyMeasurementUnit, //convertfromunit.electric,
        convertUnit
      );
    }
  };
  const utilityMeterDetail: IAny = [];
  meters.forEach((element: IAny) => {
    let rowData;

    let conversionType = ConversionType.ELECTRICITY;
    const maxUnit = getMaxBreakdownByMeter([element]);
    const maxValueUnitConversion = getMaxConvertedValueFromArray(
      conversionType,
      userPreferedUnit ? userPreferedUnit : 'kilowatt hour',
      [maxUnit],
      energyMeasurementUnit
    );
    const getTotalActualEmission = element.totalConsumption;
    const getTotalPreviousPeriodEmission = element.totalBaselineConsumption;
    let isExcludeBaseline = false;
    try {
      isExcludeBaseline =
        element.tags &&
        _.isArray(element.tags) &&
        window.ENV &&
        window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST &&
        _.some(element.tags, (t) =>
          window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST.split(',').includes(
            t
          )
        );
    } catch (e) {
      console.log('some error with exclude baseline check', e);
    }
    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(element.totalConsumption)) ||
        isNaN(Number(element.totalBaselineConsumption)) ||
        Number(element.totalConsumption) === 0 ||
        Number(element.totalBaselineConsumption) === 0 ||
        Math.abs(Number(element.totalEnergyDeviationPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    rowData = {
      meterId: element.id,
      meterName: element.name,
      utilityType: element.sourceType,
      totalActualEmission: getTotalActualEmission,
      totalPreviousPeriodEmission: getTotalPreviousPeriodEmission,
      actualUnit: getMaxConvertedValueFromArray(
        conversionType,
        userPreferedUnit ? userPreferedUnit : 'kilowatt hour',
        [element.totalConsumption],
        energyMeasurementUnit
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        conversionType,
        userPreferedUnit ? userPreferedUnit : 'kilowatt hour',
        [element.totalBaselineConsumption],
        energyMeasurementUnit
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        conversionType,
        userPreferedUnit ? userPreferedUnit : 'kilowatt hour',
        [element.totalEnergyDeviation],
        energyMeasurementUnit
      ).Unit,
      totalDeviationPercentage: element.totalEnergyDeviationPercentage,
      totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
      unit: maxValueUnitConversion.Unit,
      progessBarRelativeWidth: rawActualEmission,

      totalAbsoluteConsumption: convertElectricityValue(
        element.totalConsumption,
        maxValueUnitConversion.Unit
      ),
      totalAbsoluteBaselineConsumption: convertElectricityValue(
        element.totalBaselineConsumption,
        maxValueUnitConversion.Unit
      ),
      rawTotalActualEmission: element.totalConsumption,
      rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
      rawTotalAbsoluteConsumption: element.totalConsumption,
      rawTotalAbsoluteBaselineConsumption: element.totalBaselineConsumption,
      rawTotalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
      isExcludeBaseline
    };

    // getTotalActualEmission! > getTotalPreviousPeriodEmission!
    //       ? getTotalActualEmission
    //       : getTotalPreviousPeriodEmission,

    rowData = getRowDataForNoDeviation(
      element,
      rowData,
      conversionType,
      energyMeasurementUnit,
      maxValueUnitConversion.Unit
    );
    utilityMeterDetail.push(rowData);
  });

  return utilityMeterDetail;
};

export const getEnergyTotalUsagesAreasDetails = (
  utilityMeterDetails: IEnergyTotalUsagesAreasDetails[],
  showChangeWhenData: boolean = false
) => {
  let allUtilityAreasData: IUtilityArea[] = [];
  let progessBarRelativeWidth = Number.MIN_VALUE;
  utilityMeterDetails?.forEach((data) => {
    const { areaWiseSavingdata, utilityUnit, convertfromunit } = data;
    const convertElectricityValue = (
      value: number | null,
      convertUnit: string
    ) => {
      if (!value) {
        return value;
      } else {
        return getConvertedValuesViewDetails(
          value,
          ConversionType?.ELECTRICITY,
          utilityUnit.electric, //convertfromunit.electric,
          convertUnit
        );
      }
    };
    areaWiseSavingdata?.forEach((meters) => {
      const utilityMeterDetail: IUtilityMeterForArea[] = [];
      const parentEnergyMaxUtilityUnit = getMaxUtilityUnit(
        [meters],
        utilityUnit, //convertfromunit
        convertfromunit
      );
      meters &&
        meters.meterWiseAggregations &&
        meters.meterWiseAggregations.forEach((element) => {
          let rowData;
          // const energyMaxUtilityUnit = getMaxBreakdownByMeter(
          //   [element]
          // );
          // const parentMaxUnit = getMaxBreakdownByMeter([meters]);
          // const maxValueUnitConversion = getMaxConvertedValueFromArray(
          //   utilityMeterDetails.currentUtility,
          //   unit,
          //   [parentMaxUnit]
          // );

          let conversionType = getConversionType(element);

          // let conversionType = ConversionType.ELECTRICITY;
          // if (element.sourceType.toLowerCase() === 'gas') {
          //   conversionType = ConversionType.GAS;
          // }
          const maxUnit = getMaxBreakdownByMeter([element]);
          const maxValueUnitConversion = getMaxConvertedValueFromArray(
            conversionType,
            convertfromunit.electric,
            [maxUnit],
            utilityUnit.electric
          );
          const getTotalActualEmission = element.totalConsumption;
          const getTotalPreviousPeriodEmission =
            element.totalBaselineConsumption;

          let isExcludeBaseline = false;
          try {
            isExcludeBaseline =
              element.tags &&
              _.isArray(element.tags) &&
              window.ENV &&
              window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST &&
              _.some(element.tags, (t) =>
                window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST.split(
                  ','
                ).includes(t)
              );
          } catch (e) {
            console.log('some error with exclude baseline check', e);
          }
          const hasBaseline =
            showChangeWhenData &&
            (isNaN(Number(element.totalConsumption)) ||
              isNaN(Number(element.totalBaselineConsumption)) ||
              Number(element.totalConsumption) === 0 ||
              Number(element.totalBaselineConsumption) === 0 ||
              Math.abs(Number(element.totalEnergyDeviationPercentage)) >
                PERCENT_CHANGE_THRESHOLD);

          rowData = {
            meterId: element.id,
            meterName: element.name,
            utilityType: element.sourceType,
            totalActualEmission: getTotalActualEmission,
            totalPreviousPeriodEmission: getTotalPreviousPeriodEmission,
            actualUnit: getMaxConvertedValueFromArray(
              conversionType,
              convertfromunit.electric,
              [element.totalConsumption],
              utilityUnit.electric
            ).Unit,
            baselineUnit: getMaxConvertedValueFromArray(
              conversionType,
              convertfromunit.electric,
              [element.totalBaselineConsumption],
              utilityUnit.electric
            ).Unit,
            changeUnit: getMaxConvertedValueFromArray(
              conversionType,
              convertfromunit.electric,
              [element.totalEnergyDeviation],
              utilityUnit.electric
            ).Unit,
            totalDeviationPercentage: getTotalDeviationPercentage(element),
            // element.totalNativeEnergyDeviationPercentage === null
            //   ? null
            //   : parseFloat(
            //       element.totalNativeEnergyDeviationPercentage?.toFixed(
            //         KPI_PERCENTAGE_DECIMAL_PRECISION
            //       )
            //     ),
            totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
            unit: maxValueUnitConversion.Unit,
            progessBarRelativeWidth:
              getTotalActualEmission! > getTotalPreviousPeriodEmission!
                ? getTotalActualEmission
                : getTotalPreviousPeriodEmission,
            totalAbsoluteConsumption: convertElectricityValue(
              element.totalAbsoluteConsumption,
              maxValueUnitConversion.Unit
            ),
            totalAbsoluteBaselineConsumption: convertElectricityValue(
              element.totalAbsoluteBaselineConsumption,
              maxValueUnitConversion.Unit
            ),
            rawTotalActualEmission: element.totalConsumption,
            rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
            rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
            rawTotalAbsoluteBaselineConsumption:
              element.totalAbsoluteBaselineConsumption,
            rawTotalDeviation: hasBaseline
              ? null
              : element.totalEnergyDeviation,
            isExcludeBaseline
          };

          rowData = getRowDataForNoDeviation(
            element,
            rowData,
            conversionType,
            utilityUnit.electric,
            maxValueUnitConversion.Unit
          );
          // if (
          //   rowData.totalDeviation === 0 &&
          //   element.totalNativeEnergyDeviation !== 0
          // ) {
          //   rowData = {
          //     ...rowData,
          //     deviationScaledData: getConvertedValue(
          //       conversionType,
          //       convertfromunit.electric,
          //       element.totalNativeEnergyDeviation
          //     )
          //   };
          // }
          utilityMeterDetail.push(rowData as IUtilityMeterForArea);
        });
      let utilityData;
      console.log('meters', meters);
      let conversionType = ConversionType.ELECTRICITY;
      const hasMetersBaseline =
        showChangeWhenData &&
        (isNaN(Number(meters.totalConsumption)) ||
          isNaN(Number(meters.totalBaselineConsumption)) ||
          Number(meters.totalConsumption) === 0 ||
          Number(meters.totalBaselineConsumption) === 0 ||
          Math.abs(Number(meters.totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);

      utilityData = {
        totalActualEmission: meters.totalConsumption,
        totalPreviousPeriodEmission: meters.totalBaselineConsumption,
        totalAbsoluteConsumption: convertElectricityValue(
          meters.totalConsumption,
          parentEnergyMaxUtilityUnit.maxValueUnitConversionElectric?.Unit
        ),
        totalAbsoluteBaselineConsumption: convertElectricityValue(
          meters.totalBaselineConsumption,
          parentEnergyMaxUtilityUnit.maxValueUnitConversionElectric?.Unit
        ),
        percentageEmissionsSaving: getPercentageEmissionSavings(meters),
        // meters.totalNativeEnergyDeviationPercentage === null
        //   ? null
        //   : parseFloat(
        //       meters.totalNativeEnergyDeviationPercentage?.toFixed(
        //         KPI_PERCENTAGE_DECIMAL_PRECISION
        //       )
        //     ),
        totalDeviation: hasMetersBaseline ? null : meters.totalEnergyDeviation,
        actualUnit: getMaxConvertedValueFromArray(
          conversionType,
          convertfromunit.electric,
          [meters.totalConsumption],
          utilityUnit.electric
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          conversionType,
          convertfromunit.electric,
          [meters.totalBaselineConsumption],
          utilityUnit.electric
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          conversionType,
          convertfromunit.electric,
          [meters.totalEnergyDeviation],
          utilityUnit.electric
        ).Unit,
        rawTotalActualEmission: meters.totalConsumption,
        rawTotalPreviousPeriodEmission: meters.totalBaselineConsumption,
        rawTotalAbsoluteConsumption: meters.totalConsumption,
        rawTotalAbsoluteBaselineConsumption: meters.totalBaselineConsumption,
        rawTotalDeviation: hasMetersBaseline
          ? null
          : meters.totalEnergyDeviation,
        utilityType: meters.name,
        utilityMeters: utilityMeterDetail,
        unit: parentEnergyMaxUtilityUnit.maxValueUnitConversionElectric?.Unit,
        totalItems: meters.totalItems,
        id: meters.id
      };
      progessBarRelativeWidth = Math.max(
        progessBarRelativeWidth,
        utilityData.rawTotalActualEmission!,
        utilityData.rawTotalPreviousPeriodEmission!
      );
      (utilityData = getUtilityDataForNoDeviation(
        meters,
        utilityData,
        utilityUnit,
        parentEnergyMaxUtilityUnit.maxValueUnitConversionElectric?.Unit
      )),
        // if (
        //   utilityData.totalDeviation === 0 &&
        //   meters.totalNativeEnergyDeviation !== 0
        // ) {
        //   utilityData = {
        //     ...utilityData,
        //     deviationScaledData: getConvertedValue(
        //       ConversionType?.ELECTRICITY,
        //       convertfromunit.electric,
        //       meters.totalNativeEnergyDeviation
        //     )
        //   };
        // }
        allUtilityAreasData.push(utilityData as IUtilityArea);
    });
  });
  allUtilityAreasData = addRealtiveProgressbarField(
    allUtilityAreasData,
    progessBarRelativeWidth
  );
  return allUtilityAreasData;
};

const addRealtiveProgressbarField = (
  allUtilityAreasData: IUtilityArea[],
  progessBarRelativeWidth: number
) => {
  return allUtilityAreasData.map((area) => {
    return {
      ...area,
      utilityMeters: area.utilityMeters.map((meter) => {
        return {
          ...meter,
          progessBarRelativeWidth: progessBarRelativeWidth
        };
      }),
      progessBarRelativeWidth: progessBarRelativeWidth
    };
  });
};

export const getEnergyTotalUsagesAreasDetailsForOthersTabs = (
  utilityMeterDetails: IEnergyDetailsForOtherTabs,
  showChangeWhenData: boolean = false
) => {
  let allUtilityAreasData: IUtilityArea[] = [];
  let convertedUnit = '';
  let progessBarRelativeWidth = Number.MIN_VALUE;
  const {
    areaWiseSavingdata,
    currentUtility,
    unit,
    convertfromunit
  } = utilityMeterDetails;

  areaWiseSavingdata &&
    areaWiseSavingdata.forEach((meters) => {
      const convertElectricValue = (
        value: number | null,
        convertUnit: string
      ) => {
        if (!value) return value;
        else {
          return getConvertedValue(
            ConversionType?.ELECTRICITY,
            convertfromunit,
            value,
            convertUnit
          ).Value;
        }
      };

      const convertGasValue = (value: number | null, convertUnit: string) => {
        return convertGasValueCheck(
          ConversionType?.GAS,
          convertfromunit,
          value,
          convertUnit
        );
      };
      const convertWaterValue = (value: number | null, convertUnit: string) => {
        return convertGasValueCheck(
          ConversionType?.WATER,
          convertfromunit,
          value,
          convertUnit
        );
      };
      const utilityMeterDetail: IUtilityMeterForArea[] = [];
      meters.meterWiseAggregations.forEach((element) => {
        const maxUnit = getMaxBreakdownByMeter([element]);
        const maxValueUnitConversion = getMaxConvertedValueFromArray(
          utilityMeterDetails.currentUtility,
          unit,
          [maxUnit],
          convertfromunit
        );
        convertedUnit = maxValueUnitConversion.Unit;
        let rowData;
        let isExcludeBaseline = false;
        try {
          isExcludeBaseline =
            element.tags &&
            _.isArray(element.tags) &&
            window.ENV &&
            window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST &&
            _.some(element.tags, (t) =>
              window.ENV.METER_ELEMENT_EXCLUDE_BASELINE_CS_LIST.split(
                ','
              ).includes(t)
            );
        } catch (e) {
          console.log('some error with exclude baseline check', e);
        }
        if (
          element.sourceType.toLowerCase() === EnergyUsageScopeType.ELECTRIC ||
          element.sourceType === EnergyUsageScopeType.ELECTRICITY
        ) {
          const hasBaseline =
            showChangeWhenData &&
            (isNaN(Number(element.totalConsumption)) ||
              isNaN(Number(element.totalBaselineConsumption)) ||
              Number(element.totalConsumption) === 0 ||
              Number(element.totalBaselineConsumption) === 0 ||
              Math.abs(Number(element.totalEnergyDeviationPercentage)) >
                PERCENT_CHANGE_THRESHOLD);
          rowData = {
            meterId: element.id,
            meterName: element.name,
            utilityType: element.sourceType,
            totalActualEmission: element.totalConsumption,
            actualUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalConsumption],
              convertfromunit
            ).Unit,
            baselineUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalBaselineConsumption],
              convertfromunit
            ).Unit,
            changeUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalEnergyDeviation],
              convertfromunit
            ).Unit,
            totalPreviousPeriodEmission: element.totalBaselineConsumption,
            totalAbsoluteBaselineConsumption: convertElectricValue(
              element.totalBaselineConsumption,
              maxValueUnitConversion.Unit
            ),
            totalAbsoluteConsumption: convertElectricValue(
              element.totalConsumption,
              maxValueUnitConversion.Unit
            ),
            totalDeviationPercentage: totalDeviationPercentageCheck(
              element.totalEnergyDeviationPercentage
            ),
            totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
            rawTotalActualEmission: element.totalConsumption,
            rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
            rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
            rawTotalAbsoluteBaselineConsumption:
              element.totalAbsoluteBaselineConsumption,
            rawTotalDeviation: hasBaseline
              ? null
              : element.totalEnergyDeviation,
            unit: maxValueUnitConversion.Unit,
            isExcludeBaseline
          };
          rowData = getRowDataForNoDeviation(
            element,
            rowData,
            ConversionType?.ELECTRICITY,
            convertfromunit,
            maxValueUnitConversion.Unit
          );
          // if (
          //   rowData.totalDeviation === 0 &&
          //   element.totalNativeEnergyDeviation !== 0
          // ) {
          //   rowData = {
          //     ...rowData,
          //     deviationScaledData: getConvertedValue(
          //       ConversionType?.ELECTRICITY,
          //       convertfromunit,
          //       element.totalNativeEnergyDeviation
          //     )
          //   };
          // }
        }
        if (element.sourceType.toLowerCase() === 'gas') {
          const hasBaseline =
            showChangeWhenData &&
            (isNaN(Number(element.totalConsumption)) ||
              isNaN(Number(element.totalBaselineConsumption)) ||
              Number(element.totalConsumption) === 0 ||
              Number(element.totalBaselineConsumption) === 0 ||
              Math.abs(Number(element.totalEnergyDeviationPercentage)) >
                PERCENT_CHANGE_THRESHOLD);

          rowData = {
            meterId: element.id,
            meterName: element.name,
            utilityType: element.sourceType,
            totalActualEmission: element.totalConsumption,
            actualUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalConsumption],
              convertfromunit
            ).Unit,
            baselineUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalBaselineConsumption],
              convertfromunit
            ).Unit,
            changeUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalEnergyDeviation],
              convertfromunit
            ).Unit,
            totalPreviousPeriodEmission: element.totalBaselineConsumption,
            totalAbsoluteBaselineConsumption: convertGasValue(
              element.totalBaselineConsumption,
              maxValueUnitConversion.Unit
            ),
            totalAbsoluteConsumption: convertGasValue(
              element.totalConsumption,
              maxValueUnitConversion.Unit
            ),
            totalDeviationPercentage: totalDeviationPercentageCheck(
              element.totalEnergyDeviationPercentage
            ),
            totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
            rawTotalActualEmission: element.totalConsumption,
            rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
            rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
            rawTotalAbsoluteBaselineConsumption:
              element.totalAbsoluteBaselineConsumption,
            rawTotalDeviation: hasBaseline
              ? null
              : element.totalEnergyDeviation,
            unit: maxValueUnitConversion.Unit,
            isExcludeBaseline
          };

          rowData = getRowDataForNoDeviation(
            element,
            rowData,
            ConversionType?.GAS,
            convertfromunit
          );
          // if (
          //   rowData.totalDeviation === 0 &&
          //   element.totalNativeEnergyDeviation !== 0
          // ) {
          //   rowData = {
          //     ...rowData,
          //     deviationScaledData: getConvertedValue(
          //       ConversionType?.GAS,
          //       convertfromunit,
          //       element.totalNativeEnergyDeviation
          //     )
          //   };
          // }
        }
        if (element.sourceType.toLowerCase() === 'water') {
          const hasBaseline =
            showChangeWhenData &&
            (isNaN(Number(element.totalConsumption)) ||
              isNaN(Number(element.totalBaselineConsumption)) ||
              Number(element.totalConsumption) === 0 ||
              Number(element.totalBaselineConsumption) === 0 ||
              Math.abs(Number(element.totalEnergyDeviationPercentage)) >
                PERCENT_CHANGE_THRESHOLD);
          rowData = {
            meterId: element.id,
            meterName: element.name,
            utilityType: element.sourceType,
            totalActualEmission: element.totalConsumption,
            actualUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalConsumption],
              convertfromunit
            ).Unit,
            baselineUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalBaselineConsumption],
              convertfromunit
            ).Unit,
            changeUnit: getMaxConvertedValueFromArray(
              utilityMeterDetails.currentUtility,
              unit,
              [element.totalEnergyDeviation],
              convertfromunit
            ).Unit,
            totalPreviousPeriodEmission: element.totalBaselineConsumption,
            totalAbsoluteBaselineConsumption: convertGasValue(
              element.totalBaselineConsumption,
              maxValueUnitConversion.Unit
            ),
            totalAbsoluteConsumption: convertGasValue(
              element.totalConsumption,
              maxValueUnitConversion.Unit
            ),
            totalDeviationPercentage: totalDeviationPercentageCheck(
              element.totalEnergyDeviationPercentage
            ),
            totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
            rawTotalActualEmission: element.totalConsumption,
            rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
            rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
            rawTotalAbsoluteBaselineConsumption:
              element.totalAbsoluteBaselineConsumption,
            rawTotalDeviation: hasBaseline
              ? null
              : element.totalEnergyDeviation,
            unit: maxValueUnitConversion.Unit
          };

          rowData = getRowDataForNoDeviation(
            element,
            rowData,
            ConversionType.WATER,
            convertfromunit
          );
        }
        utilityMeterDetail.push(rowData as IUtilityMeterForArea);
      });
      const parentMaxUnit = getMaxBreakdownByMeter([meters]);
      const parentMaxValueUnitConversion = getMaxConvertedValueFromArray(
        utilityMeterDetails.currentUtility,
        currentUtility === ConversionType.GAS ||
          currentUtility == ConversionType.WATER
          ? convertfromunit
          : unit,
        [parentMaxUnit],
        convertfromunit
      );
      const unitConversionMethod =
        currentUtility === ConversionType.GAS
          ? convertGasValue
          : currentUtility === ConversionType.WATER
          ? convertWaterValue
          : convertElectricValue;
      let utilityData;
      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(meters.totalConsumption)) ||
          isNaN(Number(meters.totalBaselineConsumption)) ||
          Number(meters.totalConsumption) === 0 ||
          Number(meters.totalBaselineConsumption) === 0 ||
          Math.abs(Number(meters.totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);
      utilityData = {
        totalActualEmission: meters.totalConsumption,
        totalPreviousPeriodEmission: meters.totalBaselineConsumption,
        totalItems: meters.totalItems,
        actualUnit: getMaxConvertedValueFromArray(
          utilityMeterDetails.currentUtility,
          unit,
          [meters.totalConsumption],
          convertfromunit
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          utilityMeterDetails.currentUtility,
          unit,
          [meters.totalBaselineConsumption],
          convertfromunit
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          utilityMeterDetails.currentUtility,
          unit,
          [meters.totalEnergyDeviation],
          convertfromunit
        ).Unit,
        totalAbsoluteBaselineConsumption: unitConversionMethod(
          meters.totalBaselineConsumption,
          parentMaxValueUnitConversion.Unit
        ),
        totalAbsoluteConsumption: unitConversionMethod(
          meters.totalConsumption,
          parentMaxValueUnitConversion.Unit
        ),
        percentageEmissionsSaving: totalDeviationPercentageCheck(
          meters.totalEnergyDeviationPercentage
        ),
        totalDeviation: hasBaseline ? null : meters.totalEnergyDeviation,
        rawTotalActualEmission: meters.totalConsumption,
        rawTotalPreviousPeriodEmission: meters.totalBaselineConsumption,
        rawTotalAbsoluteConsumption: meters.totalConsumption,
        rawTotalAbsoluteBaselineConsumption: meters.totalBaselineConsumption,
        rawTotalDeviation: hasBaseline ? null : meters.totalEnergyDeviation,
        utilityType: meters.name,
        utilityMeters: utilityMeterDetail,
        unit: parentMaxValueUnitConversion.Unit,
        id: meters.id
      };
      utilityData = getUtilityDataForNoDeviationForOtherTabs(
        meters,
        utilityData,
        convertfromunit,
        currentUtility,
        parentMaxValueUnitConversion.Unit
      );
      // if (
      //   utilityData.totalDeviation === 0 &&
      //   meters.totalNativeEnergyDeviation !== 0
      // ) {
      //   utilityData = {
      //     ...utilityData,
      //     deviationScaledData: getConvertedValue(
      //       currentUtility === ConversionType.GAS
      //         ? ConversionType.GAS
      //         : ConversionType.ELECTRICITY,
      //       convertfromunit,
      //       meters.totalNativeEnergyDeviation
      //     )
      //   };
      // }
      progessBarRelativeWidth = Math.max(
        progessBarRelativeWidth,
        utilityData.rawTotalActualEmission!,
        utilityData.rawTotalAbsoluteBaselineConsumption!
      );
      allUtilityAreasData.push(utilityData as IUtilityArea);
    });
  allUtilityAreasData = addRealtiveProgressbarField(
    allUtilityAreasData,
    progessBarRelativeWidth
  );
  return { values: allUtilityAreasData, displayUnit: convertedUnit };
};

export const convertDataForAreaWiseEnergyUsagesSavingsMeter = (
  utilityMeters: IEnergyMeter[],
  unit: string,
  areaWiseAggregations: AreaWiseAggregations[],
  energyMeasurementUnit: string,
  sourceType: string,
  showChangeWhenData: boolean = false
) => {
  let conversionType = ConversionType.ELECTRICITY;
  if (sourceType.toLowerCase() === 'gas') {
    conversionType = ConversionType.GAS;
  }
  if (sourceType.toLowerCase() === 'water') {
    conversionType = ConversionType.WATER;
  }

  const totalConsumeUtilitydetails: IEnergyDetailsForOtherTabs = {
    areaWiseSavingdata: areaWiseAggregations,
    currentUtility: conversionType,
    unit,
    convertfromunit: energyMeasurementUnit
  };
  return getEnergyTotalUsagesAreasDetailsForOthersTabs(
    totalConsumeUtilitydetails,
    showChangeWhenData
  );
};
